import React, { useCallback, useEffect, useState } from "react";
import { PartialNullable } from "../../types";
import { Contractor, DraftAlbum, Genre, MultiTrackTableItem, useMusicApi } from "../../api/useMusicApi";
import { UpdateFields } from "../../hooks/use-advanced-state";
import { useWizard, Wizard } from "react-use-wizard";
import { MainAlbumInformation } from "./wizard/Album/MainAlbumInformation";
import { MainTracksInformation } from "./wizard/Tracks/MainTracksInformation";
import cn from "classnames";
import { useTranslation } from "../../hooks/useTranslation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons/faSpinner";
import { faShare } from "@fortawesome/free-solid-svg-icons";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { useLoading } from "../../hooks/use-loading";
import { toast } from "react-toastify";
import { useModalConfirmAction } from "../../components/Modals/ModalConfirmActionContext";
import { faEdit } from "@fortawesome/free-solid-svg-icons/faEdit";
import { useNavigate } from "react-router-dom";
import { faLink } from "@fortawesome/free-solid-svg-icons/faLink";
import { faRemove } from "@fortawesome/free-solid-svg-icons/faRemove";
import { NumberParam, StringParam, useQueryParam, withDefault } from "use-query-params";
import { twMerge } from "tailwind-merge";
import { ValidationError } from "../../components/ValidationInputWrapper";
import i18next from "i18next";

import { ReactPopover } from "./wizard/Tracks/ReactPopover";
import { faXmark } from "@fortawesome/free-solid-svg-icons/faXmark";

type BaseProps = {
  genres: Array<Genre>;
  contractors: Array<Contractor>;
  onFileSelect: (file: Blob | null) => Promise<any>;
  onSaveAlbum?: () => Promise<any>;
  onSaveTracks?: (tracks: PartialNullable<MultiTrackTableItem>[]) => Promise<any>;
};

type AlbumProps = {
  isDraft: boolean;
  isChangesPending?: boolean;
  title: string;
  entity: PartialNullable<DraftAlbum>;
  onChange?: UpdateFields<PartialNullable<DraftAlbum>>;
  validationErrors: ValidationError[];
} & BaseProps;

export const WizardHeader: React.FC<{
  album: PartialNullable<DraftAlbum>;
  isDraft: boolean;
  isChangesPending: boolean;
  title: string;
}> = ({ title, album, isDraft, isChangesPending }) => {
  const { t } = useTranslation();
  const [trackId] = useQueryParam("track_id", withDefault(StringParam, null));
  const [step, setStep] = useQueryParam(
    "step",
    withDefault(NumberParam, trackId != null ? 1 : undefined)
  );
  const { activeStep, goToStep } = useWizard();
  const api = useMusicApi();
  const navigate = useNavigate();
  const [albumUrl, setAlbumUrl] = useState<string | null>(null);
  const { showConfirmActionModal } = useModalConfirmAction();
  const [errors, setErrors] = useState<string[]>([]);
  const { loadings, startLoading, stopLoading } = useLoading({
    publish: false,
    delete: false,
    moveToDraft: false,
    shortlink: false,
    takedown: false
  });

  useEffect(() => {
    if (trackId != null) {
      setTimeout(() => {
        document
          .getElementById(`track_item_${trackId}`)
          ?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [trackId]);

  useEffect(() => {
    goToStep(step ?? 0);
  }, [step]);

  useEffect(() => {
    if (activeStep === 0) {
      setStep(undefined);
    } else {
      setStep(activeStep);
    }
  }, [activeStep]);

  const handlePublish = useCallback(async () => {
    if (entity.id == null) {
      return;
    }
    try {
      if (!(await showConfirmActionModal(t("publish_album_confirmation")))) {
        return;
      }
      setErrors([]);
      startLoading("publish");
      const res = await api.postPublishAlbumDraft(entity.id);

      let wrongIdsTxt = "Wrong_id(s)_values";
      let notReadyTxt = "This_id(s)_does_not_ready";
      let noImageTxt = "Does_not_have_image";
      let noReleasesTxt = "Does_not_have_any_releases";
      let wrongTrackNumbersTxt = "Have_wrong_track_numbers_in_album";
      let doesNotHaveSomeFilesTxt = "Does_not_have_some_files";
      let externalIdAlreadyExistsTxt = "External_id_already_exists";
      let wrongFocusRulesTxt = "Wrong_focus_rules";

      const error = res as any;

      let wrongIds = error[wrongIdsTxt];

      const errors = [];
      let notReady = error[notReadyTxt];
      let noImage = notReady[noImageTxt];
      let noReleases = notReady[noReleasesTxt];
      let wrongTrackNumbers = notReady[wrongTrackNumbersTxt];
      let wrongFocusRules = notReady[wrongFocusRulesTxt];
      let doesNotHaveSomeFiles = notReady[doesNotHaveSomeFilesTxt];
      let externalIdAlreadyExists = notReady[externalIdAlreadyExistsTxt];

      if (noImage && noImage.length > 0) {
        errors.push(noImageTxt);
      }
      if (noReleases && noReleases.length > 0) {
        errors.push(noReleasesTxt);
      }
      if (wrongTrackNumbers && wrongTrackNumbers.length > 0) {
        errors.push(wrongTrackNumbersTxt);
      }
      if (wrongFocusRules.length > 0) {
        errors.push(wrongFocusRulesTxt);
      }
      if (wrongIds.length > 0) {
        errors.push(wrongIdsTxt);
      }
      if (doesNotHaveSomeFiles.length > 0) {
        errors.push(doesNotHaveSomeFilesTxt);
      }
      if (externalIdAlreadyExists.length > 0) {
        errors.push(externalIdAlreadyExistsTxt);
      }

      if (errors.length === 0) {
        navigate("/music/drafts/");
        toast.success(t("publish_album_success"));
      } else {
        setErrors(errors);
      }
    } catch (e) {
      toast.error(t("publish_album_error"));
    } finally {
      stopLoading("publish");
    }
  }, []);

  const handleDelete = useCallback(async () => {
    if (entity.id == null) {
      return;
    }
    try {
      if (!(await showConfirmActionModal(t("draft_delete_confirmation")))) {
        return;
      }
      startLoading("delete");
      await api.deleteDraftAlbum(entity.id);
      navigate("/music/drafts/");
      toast.success(t("delete_draft_success"));
    } catch (e) {
      toast.error(t("delete_draft_error"));
    } finally {
      stopLoading("delete");
    }
  }, []);

  const handleEditAlbum = useCallback(async () => {
    try {
      if (!(await showConfirmActionModal(`${t("confirmation_move_draft")}?`))) {
        return;
      }
      startLoading("moveToDraft");
      const res = await api.postMoveAlbumToDraft(
        album.id as any,
        album?.contractor_id as any
      );
      navigate(`/music/drafts/${res.id}`);
      toast.success(`${t("album_moved")}`);
    } catch (e) {
      toast.error(`${t("fail_move_album")}`);
    } finally {
      stopLoading("moveToDraft");
    }
  }, []);

  const handleGetUrl = useCallback(async () => {
    const showUrlBlockInsteadCopyToClipboard = (window as any).safari !== undefined || !("clipboard" in navigator);

    try {
      startLoading("shortlink");
      const res = await api.getAlbumURL(album.id as any);
      if (res?.shortlink === "" || res?.shortlink == null) {
        toast.warning(`${t("empty_copy_link")}`);
        return;
      }

      if (showUrlBlockInsteadCopyToClipboard) {
        setAlbumUrl(res?.shortlink as string);
        return;
      }

      navigator.clipboard.writeText(res.shortlink).then(
        function() {
          toast.success(`${t("link_copy")}`);
        },
        function() {
          toast.warning(`${t("fail_copy_link")}`);
        }
      );
    } catch (e) {
      toast.error(`${t("something_went_wrong")}!`);
    } finally {
      stopLoading("shortlink");
    }
  }, []);

  const handleTakeDownAlbum = useCallback(async () => {
    try {
      if (
        !(await showConfirmActionModal(
          `${t("confirmation_take_down_product")}?`
        ))
      ) {
        return;
      }
      startLoading("takedown");
      await api.postTearDownAlbum(album.id as any);
      toast.success(`${t("revocation_process")}`);
    } catch (e) {
      toast.error(`${t("something_went_wrong")}!`);
    } finally {
      stopLoading("takedown");
    }
  }, []);

  const entity = album;
  return (
    <>
      <div className={"mb-3 text-2xl font-bold"}>{title}</div>
      {errors.length > 0 && (
        <div className={"rounded border p-3 mb-3 text-white bg-red-500"}>
          <ul className={"m-0 p-0"}>
            {errors.map((t) => (
              <li>{i18next.t(t)}</li>
            ))}
          </ul>
        </div>
      )}

      {
        albumUrl && <div className={"h-[50px] my-3 rounded px-3 bg-blue-50 flex justify-between gap-1 items-center"}>
          <div>
            <span className={"text-gray-500 font-medium"}>{t("album_url")}:</span>&nbsp;
            <a
              href={albumUrl}
              target="_blank"
              rel="noopener noreferrer">
              {albumUrl}
            </a>
          </div>
          <div>
            <FontAwesomeIcon
              icon={faXmark}
              className={"text-gray-500 cursor-pointer"}
              onClick={() => setAlbumUrl(null)}
            />
          </div>
        </div>
      }

      <div className={"flex align-items-center justify-between"}>
        <ul className="flex flex-wrap text-md font-medium text-center m-0 p-0 text-gray-500 dark:text-gray-400">
          <li className={cn("me-2")} onClick={() => goToStep(0)}>
            <span
              aria-current="page"
              style={{
                position: "relative",
                bottom: "-2px",
                backgroundColor: "white"
              }}
              className={twMerge(
                cn(
                  "inline-block p-3 border-r-2 border-l-2 border-t-2 rounded-t-lg cursor-pointer active dark:bg-gray-800 dark:text-blue-500",
                  {
                    "bg-gray-50": activeStep !== 0,
                    "border-0": activeStep !== 0
                  }
                )
              )}
            >
              {t("main_information")}
            </span>
          </li>
          <li
            className={cn("me-2")}
            title={
              entity?.id != null
                ? undefined
                : t("track_lists_allowed_after_save_album").toString()
            }
            onClick={
              entity.id != null
                ? () => goToStep(1)
                : () => toast.error(t("track_lists_allowed_after_save_album"))
            }
          >
            <span
              style={{
                position: "relative",
                bottom: "-2px",
                backgroundColor: "white"
              }}
              className={twMerge(
                cn(
                  "inline-block p-3 border-r-2 border-l-2 border-t-2 rounded-t-lg cursor-pointer dark:hover:bg-gray-800 dark:hover:text-gray-300",
                  {
                    "bg-gray-50": activeStep !== 1,
                    "border-0": activeStep !== 1,
                    "cursor-not-allowed": entity?.id == null
                  }
                )
              )}
            >
              {t("track_list")} ({album.trackrelease_set?.length ?? 0})
            </span>
          </li>
        </ul>
        <div>
          {isDraft && entity.id != null && (
            <>
              <ReactPopover
                trigger={"hover"}
                direction={"bottom"}
                className={"inline-flex text-muted text-xs"}
                content={t("publish_action_hint")}
              >
                <button
                  type="button"
                  disabled={loadings.publish || isChangesPending}
                  className="disabled:cursor-not-allowed inline-flex items-center px-3 py-1 text-xs font-medium text-gray-900 dark:text-white bg-transparent border border-gray-900 rounded-l-lg hover:bg-gray-900 focus:z-10 focus:ring-2 focus:ring-gray-500 focus:bg-gray-900"
                  onClick={handlePublish}
                >
                  <FontAwesomeIcon
                    icon={loadings.publish ? faSpinner : faShare}
                    spin={loadings.publish}
                    className="w-3 h-3 mr-2 fill-current"
                  />
                  {t("publish")}
                </button>
              </ReactPopover>
              <ReactPopover
                className={"inline-flex text-muted text-xs"}
                trigger={"hover"}
                content={t("delete_draft_action_hint")}
              >
                <button
                  type="button"
                  disabled={loadings.delete || isChangesPending}
                  className="disabled:cursor-not-allowed inline-flex items-center px-3 py-1 text-xs font-medium text-gray-900 dark:text-white bg-transparent border border-gray-900 rounded-r-md hover:bg-gray-900 focus:z-10 focus:ring-2 focus:ring-gray-500 focus:bg-gray-900"
                  onClick={handleDelete}
                >
                  <FontAwesomeIcon
                    icon={loadings.delete ? faSpinner : faTrash}
                    spin={loadings.delete}
                    className="w-3 h-3 mr-2 fill-current"
                  />
                  {t("delete")}
                </button>
              </ReactPopover>
            </>
          )}

          {!isDraft && entity.id != null && (
            <>
              <button
                type="button"
                disabled={loadings.moveToDraft || isChangesPending}
                className="disabled:cursor-not-allowed inline-flex items-center px-3 py-1 text-xs font-medium text-gray-900 dark:text-white bg-transparent border border-gray-900 rounded-l-lg hover:bg-gray-900 focus:z-10 focus:ring-2 focus:ring-gray-500 focus:bg-gray-900"
                onClick={handleEditAlbum}
              >
                <FontAwesomeIcon
                  icon={loadings.moveToDraft ? faSpinner : faEdit}
                  spin={loadings.moveToDraft}
                  className="w-3 h-3 mr-2 fill-current"
                />
                {t("draft")}
              </button>
              {entity?.content_type === "audio" && (
                <button
                  type="button"
                  className="disabled:cursor-not-allowed inline-flex items-center px-3 py-1 text-xs font-medium text-gray-900 dark:text-white bg-transparent border border-gray-900 hover:bg-gray-900 focus:z-10 focus:ring-2 focus:ring-gray-500 focus:bg-gray-900"
                  onClick={handleGetUrl}
                >
                  <FontAwesomeIcon
                    icon={loadings.shortlink ? faSpinner : faLink}
                    spin={loadings.shortlink}
                    className="w-3 h-3 mr-2 fill-current"
                  />
                  {t("url")}
                </button>
              )}
              <button
                type="button"
                className="disabled:cursor-not-allowed inline-flex items-center px-3 py-1 text-xs font-medium text-gray-900 dark:text-white bg-transparent border border-gray-900 rounded-r-md hover:bg-gray-900 focus:z-10 focus:ring-2 focus:ring-gray-500 focus:bg-gray-900"
                onClick={handleTakeDownAlbum}
              >
                <FontAwesomeIcon
                  icon={loadings.takedown ? faSpinner : faRemove}
                  spin={loadings.takedown}
                  className="w-3 h-3 mr-2 fill-current"
                />
                {t("take_down_product")}
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export const EditDraftForm: React.FC<AlbumProps> = ({
                                                      isDraft,
                                                      isChangesPending,
                                                      title,
                                                      entity,
                                                      genres,
                                                      contractors,
                                                      validationErrors,
                                                      onChange,
                                                      onFileSelect,
                                                      onSaveAlbum,
                                                      onSaveTracks,
                                                    }) => {
  const [trackId] = useQueryParam("track_id", withDefault(StringParam, null));
  const [step] = useQueryParam(
    "step",
    withDefault(NumberParam, trackId != null ? 1 : undefined)
  );

  return (
    <div className={"dark:text-white rounded-lg"}>
      <Wizard
        startIndex={step}
        header={<WizardHeader
          title={title}
          album={entity}
          isDraft={isDraft}
          isChangesPending={isChangesPending ?? false}
        />}
      >
        <MainAlbumInformation
          isDraft={isDraft}
          entity={entity}
          genres={genres}
          contractors={contractors}
          onChange={onChange as any}
          validationErrors={validationErrors}
          onSaveAlbum={onSaveAlbum as any}
          onFileSelect={onFileSelect}
        />
        <MainTracksInformation
          isDraft={isDraft}
          album={entity}
          genres={genres}
          onSave={onSaveTracks}
          onChange={onChange as any}
        />
      </Wizard>
    </div>
  );
};
